// src/utils/checkinFlow.js
// Screen sequence + guest-entry rules for the open house check-in kiosk.
// Pure functions only — the kiosk page renders whatever this returns.

import { chicagoHHMM, chicagoYMD } from "./chicagoTime";

export const CORE_INTENTS = [
  { value: "buy", label: "Looking to buy" },
  { value: "sell", label: "I have a home to sell" },
  { value: "both", label: "Both" },
  { value: "browsing", label: "Just browsing" },
];

export const PARTY_CHOICES = [1, 2, 3, 4, 5, 6];

// The built-in "Standard" set. Not stored in Firestore; always offered.
export const DEFAULT_TEMPLATE = {
  id: "standard",
  name: "Standard",
  askAgentStatus: true,
  questions: [],
};

// Pre-built question set every agent gets without building anything: the
// extras agents most want answered at an open house. Also not stored in
// Firestore, so the wording can improve with the app.
export const RECOMMENDED_TEMPLATE = {
  id: "recommended",
  name: "Recommended",
  askAgentStatus: true,
  questions: [
    {
      id: "rec_source",
      type: "choice",
      prompt: "How did you hear about this open house?",
      options: ["Saw the sign", "Zillow or online", "Social media", "Friend or neighbor", "I live nearby"],
      skippable: true,
    },
    {
      id: "rec_timeline",
      type: "choice",
      prompt: "How soon are you looking to make a move?",
      options: ["Right away", "1 to 3 months", "3 to 6 months", "Just exploring"],
      skippable: true,
    },
    {
      id: "rec_preapproved",
      type: "yesno",
      prompt: "Are you pre-approved for a mortgage?",
      skippable: true,
    },
    {
      id: "rec_price",
      type: "choice",
      prompt: "What did you think of the price?",
      options: ["Feels right", "A bit high", "A great value"],
      skippable: true,
    },
  ],
};

export const BUILT_IN_TEMPLATES = [DEFAULT_TEMPLATE, RECOMMENDED_TEMPLATE];

// Template -> ordered screen descriptors. Core screens are not removable:
// welcome/name/contact stay (lead essentials), intent powers pipeline
// sorting, party powers traffic counts. Only the agent question toggles and
// custom questions append.
export function buildKioskScreens(template) {
  const t = template || DEFAULT_TEMPLATE;
  const screens = [
    { key: "welcome", type: "welcome" },
    { key: "name", type: "name" },
    { key: "contact", type: "contact" },
  ];
  if (t.askAgentStatus !== false) screens.push({ key: "agent", type: "agent" });
  screens.push({ key: "intent", type: "intent" });
  screens.push({ key: "party", type: "party" });
  (t.questions || []).forEach((q) => {
    if (q && q.id && q.prompt)
      screens.push({ key: `q_${q.id}`, type: "custom", question: q });
  });
  screens.push({ key: "thanks", type: "thanks" });
  return screens;
}

// Minimal openHouses fields for a kiosk started WITHOUT the scheduler. The
// agent types just an address; everything downstream (counters, Sort & Send,
// feedback prefill) hangs off the resulting doc like any scheduled open
// house. The Firestore helper stamps id/feedbackToken/createdAt on top.
export function adHocOpenHouseFields(address, user, now = new Date()) {
  const agentName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.name || "";
  return {
    dealId: "",
    adHoc: true,
    propertyAddress: String(address || "").trim(),
    // Minnesota's date and time, whatever zone the iPad is set to.
    date: chicagoYMD(now),
    startTime: chicagoHHMM(now),
    endTime: "",
    hostingAgentId: user?.id || "",
    hostingAgentName: agentName,
    hostingAgentEmail: user?.email || "",
    listingAgentId: user?.id || "",
    listingAgentName: agentName,
    sellerName: "",
    sellerEmail: "",
    notes: "",
    status: "Scheduled",
  };
}

export function newGuestDraft() {
  return {
    firstName: "", lastName: "", phone: "", email: "",
    hasAgent: null, agentName: "", agentBrokerage: "", agentPhone: "",
    intent: "", partySize: 1, answers: {},
  };
}

// Progressive US phone formatting for the kiosk fields: digits in,
// "(555) 201-8890" out, correct at every keystroke length.
export function formatPhoneAsTyped(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export const canLeaveName = (d) => !!String(d?.firstName || "").trim();
export const canLeaveContact = (d) =>
  !!(String(d?.phone || "").trim() || String(d?.email || "").trim());
